/**
 * Authentication Provider
 * Handles MSAL authentication and token management
 * Includes guard token system for API authorization
 */

import { FC, ReactNode, useEffect, useCallback } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useSetRecoilState } from 'recoil';
import { apiRequest, apiConfig } from './authConfig';
import { tokenState, userInfoState, isAuthenticatedState, guardState } from './authState';
import { msalInstance } from './msalInstance';

interface AuthProviderProps {
  children: ReactNode;
}

const AuthStateManager: FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const setToken = useSetRecoilState(tokenState);
  const setUserInfo = useSetRecoilState(userInfoState);
  const setIsAuthenticated = useSetRecoilState(isAuthenticatedState);
  const setGuard = useSetRecoilState(guardState);

  // Sign guard token with the API
  const signGuard = useCallback(async (accessToken: string) => {
    try {
      console.log('🛡️ Signing guard token...');
      const response = await fetch(`${apiConfig.baseUrl}/guard/sign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'User-Agent': navigator.userAgent,
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Guard sign failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Guard token signed successfully');
      setGuard({ header: data.header, exp: data.exp });
      return data;
    } catch (error) {
      console.error('❌ Error signing guard:', error);
      setGuard({ header: null, exp: null });
      throw error;
    }
  }, [setGuard]);

  const acquireToken = useCallback(async () => {
    if (accounts.length === 0) return;

    try {
      console.log('🔑 Acquiring API token with scopes:', apiRequest.scopes);
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });

      console.log('✅ API Token acquired successfully');
      setToken(response.accessToken);
      setUserInfo({
        id: response.account?.localAccountId || '',
        email: response.account?.username || '',
        name: response.account?.name || '',
        tenantId: response.account?.tenantId || '',
      });
      setIsAuthenticated(true);

      // Sign guard after getting token
      await signGuard(response.accessToken);
    } catch (error) {
      console.error('❌ Error acquiring API token silently:', error);
      // Try interactive consent if silent fails - use redirect instead of popup
      console.log('🔄 Redirecting for interactive consent...');
      await instance.acquireTokenRedirect(apiRequest);
    }
  }, [instance, accounts, setToken, setUserInfo, setIsAuthenticated, signGuard]);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      acquireToken();
    }
  }, [isAuthenticated, accounts, acquireToken]);

  return <>{children}</>;
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthStateManager>{children}</AuthStateManager>
    </MsalProvider>
  );
};
